import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { registerUser, checkUsername, checkEmail, initializeCSRF } from "../api/api";
import { FaCheck, FaTimes, FaEye, FaEyeSlash } from "react-icons/fa";
import "./RegisterPage.css";

const RegisterPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");

  // Initialize CSRF on component mount
  useEffect(() => {
    initializeCSRF().catch(err => {
      console.error("CSRF initialization failed:", err);
      setGeneralError("Failed to initialize security. Please refresh the page.");
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear specific error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    if (generalError) {
      setGeneralError("");
    }
  };

  // Check username availability
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (formData.username.trim() !== "") {
        try {
          const res = await checkUsername(formData.username.trim());
          setUsernameAvailable(res.available);
          if (!res.available) {
            setErrors(prev => ({ ...prev, username: "Username is already taken" }));
          }
        } catch (err) {
          setUsernameAvailable(false);
          if (err.username || err.error) {
            setErrors(prev => ({ ...prev, username: err.username || err.error }));
          }
        }
      } else {
        setUsernameAvailable(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.username]);

  // Check email availability
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (formData.email.trim() !== "") {
        try {
          const res = await checkEmail(formData.email.trim());
          setEmailAvailable(res.available);
          if (!res.available) {
            setErrors(prev => ({ ...prev, email: "Email is already registered" }));
          }
        } catch (err) {
          setEmailAvailable(false);
          if (err.email || err.error) {
            setErrors(prev => ({ ...prev, email: err.email || err.error }));
          }
        }
      } else {
        setEmailAvailable(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.email]);

  // Validate password requirements
  useEffect(() => {
    setPasswordValidations({
      length: formData.password.length >= 8,
      uppercase: /[A-Z]/.test(formData.password),
      number: /[0-9]/.test(formData.password),
      specialChar: /[!@#$%^&*]/.test(formData.password),
    });
  }, [formData.password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");
    setErrors({});
    setIsLoading(true);

    // Frontend validation
    const validationErrors = {};

    if (!formData.username.trim()) {
      validationErrors.username = "Username is required";
    } else if (usernameAvailable === false) {
      validationErrors.username = "Username is not available";
    }

    if (!formData.email.trim()) {
      validationErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      validationErrors.email = "Email is invalid";
    } else if (emailAvailable === false) {
      validationErrors.email = "Email is not available";
    }

    if (!formData.password) {
      validationErrors.password = "Password is required";
    } else if (!passwordValidations.length || 
               !passwordValidations.uppercase || 
               !passwordValidations.number || 
               !passwordValidations.specialChar) {
      validationErrors.password = "Password does not meet all requirements";
    }

    if (formData.password !== formData.confirmPassword) {
      validationErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    try {
      const result = await registerUser({
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        role: "CLIENT",
      });
      
      alert(`Registration successful! Welcome ${result.user?.username || formData.username}`);
      navigate("/login", { 
        state: { 
          message: "Registration successful! Please login with your credentials." 
        } 
      });
      
    } catch (err) {
      console.error("Registration error:", err);
      
      // Handle backend validation errors
      if (typeof err === 'object') {
        if (err.email) {
          setErrors(prev => ({ ...prev, email: Array.isArray(err.email) ? err.email[0] : err.email }));
        } else if (err.username) {
          setErrors(prev => ({ ...prev, username: Array.isArray(err.username) ? err.username[0] : err.username }));
        } else if (err.password) {
          setErrors(prev => ({ ...prev, password: Array.isArray(err.password) ? err.password[0] : err.password }));
        } else if (err.detail) {
          setGeneralError(err.detail);
        } else if (err.message) {
          setGeneralError(err.message);
        } else if (err.error) {
          setGeneralError(err.error);
        } else {
          setGeneralError("Registration failed. Please try again.");
        }
      } else if (typeof err === 'string') {
        setGeneralError(err);
      } else {
        setGeneralError("Registration failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="container mt-5" style={{ maxWidth: 450 }}>
        <h2 className="mb-4 text-center">Register as Client</h2>

        {generalError && (
          <div className="alert alert-danger" role="alert">
            {generalError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Username */}
          <div className="mb-3">
            <label className="form-label">Username *</label>
            <input
              type="text"
              name="username"
              className={`form-control ${errors.username ? 'is-invalid' : ''}`}
              value={formData.username}
              onChange={handleChange}
              disabled={isLoading}
            />
            <div className="d-flex justify-content-between align-items-center mt-1">
              {formData.username && usernameAvailable !== null && (
                <small className={usernameAvailable ? "text-success" : "text-danger"}>
                  {usernameAvailable ? "✓ Available" : "✗ Already taken"}
                </small>
              )}
              {errors.username && (
                <small className="text-danger">{errors.username}</small>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="mb-3">
            <label className="form-label">Email *</label>
            <input
              type="email"
              name="email"
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
            />
            <div className="d-flex justify-content-between align-items-center mt-1">
              {formData.email && emailAvailable !== null && (
                <small className={emailAvailable ? "text-success" : "text-danger"}>
                  {emailAvailable ? "✓ Available" : "✗ Already registered"}
                </small>
              )}
              {errors.email && (
                <small className="text-danger">{errors.email}</small>
              )}
            </div>
          </div>

          {/* Password */}
          <div className="mb-3">
            <label className="form-label">Password *</label>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.password && (
              <small className="text-danger d-block mt-1">{errors.password}</small>
            )}
            <div className="mt-2">
              <small className="text-muted d-block mb-1">Password Requirements:</small>
              <div className="row">
                <div className="col-6">
                  <small className={passwordValidations.length ? "text-success" : "text-muted"}>
                    {passwordValidations.length ? <FaCheck /> : <FaTimes />} 8+ characters
                  </small>
                </div>
                <div className="col-6">
                  <small className={passwordValidations.uppercase ? "text-success" : "text-muted"}>
                    {passwordValidations.uppercase ? <FaCheck /> : <FaTimes />} Uppercase
                  </small>
                </div>
                <div className="col-6">
                  <small className={passwordValidations.number ? "text-success" : "text-muted"}>
                    {passwordValidations.number ? <FaCheck /> : <FaTimes />} Number
                  </small>
                </div>
                <div className="col-6">
                  <small className={passwordValidations.specialChar ? "text-success" : "text-muted"}>
                    {passwordValidations.specialChar ? <FaCheck /> : <FaTimes />} Special character
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="mb-4">
            <label className="form-label">Confirm Password *</label>
            <input
              type="password"
              name="confirmPassword"
              className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={isLoading}
            />
            {errors.confirmPassword && (
              <small className="text-danger">{errors.confirmPassword}</small>
            )}
          </div>

          <button 
            type="submit" 
            className="btn btn-success w-100 py-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Registering...
              </>
            ) : (
              "Register Account"
            )}
          </button>

          <div className="text-center mt-3">
            <p className="mb-0">
              Already have an account?{" "}
              <a href="/login" className="text-decoration-none">
                Login here
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
